import React from "react";
import {
  Box,
  ListItemButton,
  Avatar,
  Typography,
  Badge,
  Chip,
} from "@mui/material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { Conversation } from "shared/entities";

dayjs.extend(relativeTime);

interface ConversationItemProps {
  conversation: Conversation;
  isActive: boolean;
  onClick: (conversationId: string) => void;
}

const ConversationItem: React.FC<ConversationItemProps> = ({
  conversation,
  isActive,
  onClick,
}) => {
  const formatTimestamp = (timestamp: string): string => {
    const date = dayjs(timestamp);
    const now = dayjs();
    const diffInHours = now.diff(date, "hour");

    if (diffInHours < 1) {
      return date.fromNow(); // "a few minutes ago", "an hour ago"
    } else if (diffInHours < 24) {
      return date.format("h:mm A"); // "10:30 AM"
    } else if (diffInHours < 48) {
      return "Yesterday";
    } else if (diffInHours < 168) {
      return `${now.diff(date, "day")} days ago`;
    } else if (diffInHours < 720) {
      return `${now.diff(date, "week")} week${now.diff(date, "week") > 1 ? "s" : ""} ago`;
    } else {
      return date.format("MMM D");
    }
  };

  const getInitials = (name: string): string => {
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <ListItemButton
      onClick={() => onClick(conversation.id)}
      selected={isActive}
      sx={{
        px: 2,
        py: 1.5,
        borderRadius: 0,
        backgroundColor:
          conversation.unreadCount > 0 && !isActive
            ? "rgba(23, 139, 142, 0.05)"
            : "transparent",
        borderLeft: isActive ? "4px solid #178B8E" : "4px solid transparent",
        "&:hover": {
          backgroundColor: isActive
            ? "rgba(23, 139, 142, 0.12)"
            : "rgba(0, 0, 0, 0.04)",
        },
        "&.Mui-selected": {
          backgroundColor: "rgba(23, 139, 142, 0.08)",
          "&:hover": {
            backgroundColor: "rgba(23, 139, 142, 0.12)",
          },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          width: "100%",
          alignItems: "flex-start",
        }}
      >
        {/* Avatar with online indicator */}
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          variant="dot"
          sx={{
            "& .MuiBadge-badge": {
              backgroundColor: conversation.isOnline ? "#44b700" : "#bdbdbd",
              color: conversation.isOnline ? "#44b700" : "#bdbdbd",
              boxShadow: "0 0 0 2px #fff",
              width: 12,
              height: 12,
              borderRadius: "50%",
            },
          }}
        >
          <Avatar
            src={conversation.userAvatar}
            sx={{
              width: 48,
              height: 48,
              backgroundColor: "#178B8E",
              fontWeight: 600,
            }}
          >
            {getInitials(conversation.userName)}
          </Avatar>
        </Badge>

        {/* Conversation details */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 0.5,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: conversation.unreadCount > 0 ? 700 : 600,
                color: "text.primary",
              }}
            >
              {conversation.userName}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontSize: "0.7rem",
                whiteSpace: "nowrap",
              }}
            >
              {formatTimestamp(conversation.lastMessageTime)}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color:
                  conversation.unreadCount > 0
                    ? "text.primary"
                    : "text.secondary",
                fontWeight: conversation.unreadCount > 0 ? 600 : 400,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
                fontSize: "0.875rem",
              }}
            >
              {conversation.lastMessage}
            </Typography>
            {conversation.unreadCount > 0 && (
              <Chip
                label={
                  conversation.unreadCount > 9 ? "9+" : conversation.unreadCount
                }
                size="small"
                sx={{
                  ml: 1,
                  height: 20,
                  minWidth: 20,
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  backgroundColor: "#178B8E",
                  color: "white",
                  "& .MuiChip-label": {
                    px: 0.75,
                  },
                }}
              />
            )}
          </Box>

          {/* New message indicator */}
          {conversation.unreadCount > 0 && (
            <Box sx={{ mt: 0.5 }}>
              <Chip
                label={`${conversation.unreadCount} new`}
                size="small"
                sx={{
                  height: 18,
                  fontSize: "0.65rem",
                  fontWeight: 600,
                  backgroundColor: "#178B8E",
                  color: "white",
                  "& .MuiChip-label": {
                    px: 1,
                  },
                }}
              />
            </Box>
          )}
        </Box>
      </Box>
    </ListItemButton>
  );
};

export default ConversationItem;
